import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kSourceCaseLawMetadataV3 } from '@/constants/constants-api-paths'
import { nanoid } from 'nanoid'
import { getAuth } from 'firebase/auth'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'
import {
  CaselawMetadataReferenceSchemaV3,
  CaselawMetadataReferenceV3,
} from '@/chat-common/components/reference-viewer/caselaw/schema/caselaw-reference-viewer-v3.schema'

/**
 * Get Caselaw Metadata V3
 * @param id - The s3 path of the caselaw reference
 * @returns The caselaw metadata
 */
export async function getCaselawMetadataForStandaloneViewerV3(id: string): Promise<CaselawMetadataReferenceV3> {
  const sentry_transaction_id = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw Error(kErrorCodeAuthError)

  try {
    // Construct request
    const apiUrl = kPaxtonAppApiRESTBaseUrl() + kSourceCaseLawMetadataV3 + '?s3_path=' + id

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    }

    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()
    // Check if the response matches the schema
    const validatedData = CaselawMetadataReferenceSchemaV3.parse(data)

    if (!response.ok) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions: requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`)
    }

    return validatedData
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
